import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CampaignEvent,
  CampaignEventSource,
  CreateCampaignEventPayload,
  EligibleCampaignList,
  GetEligibleHighlightedCampaignParams,
  HighlightedCampaignEligibility
} from '../interface/campaign';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class CampaignsService {

  private readonly _isFromMatrix = true;
  private readonly _baseUrl = `${environment.mkgoURL}/api/v1/campaigns`;

  constructor(
    private http: HttpClient,
    private dataService: DataService
  ) { }

  async getEligibleHighlightedCampaign(
    params?: GetEligibleHighlightedCampaignParams
  ): Promise<HighlightedCampaignEligibility> {
    const queryParams = this.createEligibleCampaignParams(params);
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix, queryParams));

    return firstValueFrom(
      this.http.get<HighlightedCampaignEligibility>(`${this._baseUrl}/highlighted/eligible`, options)
    );
  }

  async getAllEligibleHighlightedCampaigns(
    params?: GetEligibleHighlightedCampaignParams
  ): Promise<EligibleCampaignList> {
    const queryParams = this.createEligibleCampaignParams(params);
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix, queryParams));

    return firstValueFrom(
      this.http.get<EligibleCampaignList>(`${this._baseUrl}/highlighted/eligible-list`, options)
    );
  }

  async registerEvent(
    campaignId: string,
    payload: CreateCampaignEventPayload
  ): Promise<CampaignEvent> {
    if (!campaignId?.trim()) {
      throw new Error('campaignId é obrigatório para registrar evento de campanha');
    }

    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const requestBody = {
      ...payload,
      eventSource: payload.eventSource ?? CampaignEventSource.WEB_APP,
      occurredAt: payload.occurredAt ?? new Date().toISOString()
    };

    return firstValueFrom(
      this.http.post<CampaignEvent>(`${this._baseUrl}/${campaignId}/events`, requestBody, options)
    );
  }

  private createEligibleCampaignParams(
    params?: GetEligibleHighlightedCampaignParams
  ): HttpParams | undefined {
    let httpParams = new HttpParams();

    if (typeof params?.currentState === 'number') {
      httpParams = httpParams.set('currentState', String(params.currentState));
    }

    if (params?.cnpjs?.length) {
      params.cnpjs
        .filter(cnpj => Boolean(cnpj?.trim()))
        .forEach(cnpj => {
          httpParams = httpParams.append('cnpjs', cnpj.trim());
        });
    }

    return httpParams.keys().length ? httpParams : undefined;
  }
}